import { API_BASE_URL } from '../config/api';

// System Check Utility for API Migration
// Ensures users are using the latest version with correct API endpoint

const CURRENT_API_VERSION = '2.0.0';
const EXPECTED_API_BASE = API_BASE_URL;
const VERSION_STORAGE_KEY = 'fc_app_version';
const MIGRATION_NOTICE_KEY = 'fc_migration_notice_shown';

// Try to get version from build-time generated file
let BUILD_VERSION = null;
try {
  fetch('/version.json')
    .then(res => res.json())
    .then(data => {
      BUILD_VERSION = data;
      console.log('📦 Build Version:', BUILD_VERSION);
    })
    .catch(() => console.log('⚠️ Version file not found, using default'));
} catch (e) {
  // Fallback to package.json version
}

export class SystemCheck {
  
  /**
   * Check if user is on the latest system version
   */
  static checkSystemVersion() {
    const storedVersion = localStorage.getItem(VERSION_STORAGE_KEY);
    const migrationNoticeShown = localStorage.getItem(MIGRATION_NOTICE_KEY);
    
    // If no stored version or old version, trigger migration
    if (!storedVersion || storedVersion !== CURRENT_API_VERSION) {
      return {
        needsUpdate: true,
        isFirstTime: !storedVersion,
        showNotice: !migrationNoticeShown
      };
    }
    
    return {
      needsUpdate: false,
      isFirstTime: false,
      showNotice: false
    };
  }
  
  /**
   * Validate that we're using the correct API endpoint
   */
  static validateApiEndpoint() {
    try {
      const expectedAPI = EXPECTED_API_BASE;
      const isLocal = /localhost|127\.0\.0\.1/.test(expectedAPI);
      const isCanonical =
        expectedAPI.startsWith('https://api.firstcapitalsint.com');
      return {
        isValid: isLocal || isCanonical,
        currentAPI: typeof window !== 'undefined' ? window.location.origin : '',
        expectedAPI,
      };
    } catch (error) {
      console.error('API validation error:', error);
      return {
        isValid: false,
        error: error.message
      };
    }
  }
  
  /**
   * Clear all cached data and force refresh
   */
  static async clearCacheAndRefresh() {
    try {
      // Clear localStorage except for essential data
      const essentialKeys = ['auth_token', 'user'];
      const allKeys = Object.keys(localStorage);
      
      allKeys.forEach(key => {
        if (!essentialKeys.includes(key)) {
          localStorage.removeItem(key);
        }
      });
      
      // Clear sessionStorage
      sessionStorage.clear();
      
      // Clear browser cache if supported
      if ('caches' in window) {
        const cacheNames = await caches.keys();
        await Promise.all(
          cacheNames.map(cacheName => caches.delete(cacheName))
        );
      }
      
      // Unregister service workers
      if ('serviceWorker' in navigator) {
        const registrations = await navigator.serviceWorker.getRegistrations();
        await Promise.all(
          registrations.map(registration => registration.unregister())
        );
      }
      
      return true;
    } catch (error) {
      console.error('Cache clearing error:', error);
      return false;
    }
  }
  
  /**
   * Mark migration as complete
   */
  static markMigrationComplete() {
    localStorage.setItem(VERSION_STORAGE_KEY, CURRENT_API_VERSION);
    localStorage.setItem(MIGRATION_NOTICE_KEY, 'true');
    localStorage.setItem('fc_last_migration_check', new Date().toISOString());
  }
  
  /**
   * Test connectivity to new API
   */
  static async testApiConnectivity() {
    try {
      const response = await fetch(`${EXPECTED_API_BASE}/user`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        },
        timeout: 10000
      });
      
      return {
        isConnected: response.ok,
        status: response.status,
        apiEndpoint: EXPECTED_API_BASE
      };
    } catch (error) {
      return {
        isConnected: false,
        error: error.message,
        apiEndpoint: EXPECTED_API_BASE
      };
    }
  }
  
  /**
   * Get system information for debugging
   */
  static getSystemInfo() {
    return {
      userAgent: navigator.userAgent,
      currentVersion: CURRENT_API_VERSION,
      storedVersion: localStorage.getItem(VERSION_STORAGE_KEY),
      apiEndpoint: EXPECTED_API_BASE,
      timestamp: new Date().toISOString(),
      localStorage: {
        authToken: !!localStorage.getItem('auth_token'),
        user: !!localStorage.getItem('user'),
        migrationNotice: localStorage.getItem(MIGRATION_NOTICE_KEY)
      }
    };
  }
  
  /**
   * Force application reload with cache bypass
   */
  static forceReload() {
    // Add timestamp to force cache bypass
    const url = new URL(window.location);
    url.searchParams.set('v', Date.now());
    window.location.href = url.toString();
  }
}

export default SystemCheck;
